import { useState, useEffect } from "react";
import { Activity, Radio, X } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const AdminAirplayTab = ({ authToken }) => {
  const [recentAirplays, setRecentAirplays] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [trackLog, setTrackLog] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${BACKEND_URL}/api/airplay/recent?limit=50`),
        fetch(`${BACKEND_URL}/api/airplay/summary`),
      ]);
      setRecentAirplays(await r1.json());
      setSummary(await r2.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchDetail = async (artist, title) => {
    setSelectedTrack({ artist, title });
    try {
      const res = await fetch(`${BACKEND_URL}/api/airplay/track?artist=${encodeURIComponent(artist)}&title=${encodeURIComponent(title)}`);
      setTrackLog(await res.json());
    } catch { setTrackLog([]); }
  };

  return (
    <div className="space-y-6" data-testid="admin-airplay-panel">
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div><h2 className="text-lg font-bold text-white">Airplay Analytics</h2><p className="text-white/40 text-sm mt-1">Tracks matched against approved submissions. Scanner runs every 2 minutes.</p></div>
          <button onClick={fetchData} className="text-purple-400 hover:text-purple-300 text-sm font-medium" data-testid="airplay-refresh">Refresh</button>
        </div>
        {loading ? <div className="p-12 text-center text-white/40">Loading airplay data...</div> : summary.length === 0 ? (
          <div className="p-12 text-center text-white/40"><Activity className="w-10 h-10 mx-auto mb-3 opacity-30" /><p className="font-medium text-white/50">No airplay data yet</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="airplay-summary-table">
              <thead><tr className="border-b border-white/10 text-white/40 text-xs uppercase tracking-wider"><th className="text-left p-4">Track</th><th className="text-left p-4">Stations</th><th className="text-center p-4">Plays</th><th className="text-center p-4">Listeners</th><th className="text-left p-4">Last Played</th><th className="text-center p-4">Details</th></tr></thead>
              <tbody>
                {summary.map((t, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`airplay-row-${i}`}>
                    <td className="p-4"><p className="text-white font-medium">{t.title}</p><p className="text-white/40 text-xs">{t.artist}</p></td>
                    <td className="p-4"><div className="flex flex-wrap gap-1">{t.stations.map((s, j) => <span key={j} className="text-xs px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400">{s}</span>)}</div></td>
                    <td className="p-4 text-center text-green-400 font-bold">{t.total_plays}</td>
                    <td className="p-4 text-center text-white/60">{t.total_listeners}</td>
                    <td className="p-4 text-white/40 text-xs">{t.last_played ? new Date(t.last_played * 1000).toLocaleString() : '-'}</td>
                    <td className="p-4 text-center"><button onClick={() => fetchDetail(t.artist, t.title)} className="text-purple-400 hover:text-purple-300 text-xs font-medium underline" data-testid={`airplay-detail-${i}`}>View Log</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {selectedTrack && (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="airplay-detail-panel">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div><h3 className="text-white font-bold">{selectedTrack.title}</h3><p className="text-white/40 text-sm">{selectedTrack.artist} — Play History</p></div>
            <button onClick={() => setSelectedTrack(null)} className="text-white/40 hover:text-white" data-testid="airplay-detail-close"><X className="w-5 h-5" /></button>
          </div>
          {trackLog.length === 0 ? <div className="p-8 text-center text-white/40 text-sm">No play logs found.</div> : (
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#0f0f0f]"><tr className="border-b border-white/10 text-white/40 text-xs uppercase tracking-wider"><th className="text-left p-3">Date/Time</th><th className="text-left p-3">Station</th><th className="text-left p-3">DJ / Source</th><th className="text-left p-3">Playlist</th><th className="text-center p-3">Listeners</th></tr></thead>
                <tbody>{trackLog.map((log, i) => (
                  <tr key={i} className="border-b border-white/5"><td className="p-3 text-white/50 text-xs">{new Date(log.played_at * 1000).toLocaleString()}</td><td className="p-3"><span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400">{log.station_name}</span></td><td className="p-3 text-white/60">{log.streamer || 'AutoDJ'}</td><td className="p-3 text-white/40 text-xs">{log.playlist || '-'}</td><td className="p-3 text-center text-white/50">{log.listeners}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-white/10"><h3 className="text-white font-bold">Recent Airplay Feed</h3><p className="text-white/40 text-xs mt-1">Last 50 detected plays across all stations</p></div>
        {recentAirplays.length === 0 ? <div className="p-8 text-center text-white/40 text-sm">No recent airplays detected yet.</div> : (
          <div className="max-h-96 overflow-y-auto divide-y divide-white/5">
            {recentAirplays.map((log, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-4 hover:bg-white/[0.02]" data-testid={`recent-airplay-${i}`}>
                <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0"><Radio className="w-4 h-4 text-green-400" /></div>
                <div className="flex-1 min-w-0"><p className="text-white text-sm font-medium truncate">{log.artist} — {log.title}</p><p className="text-white/40 text-xs">{log.station_name} · {log.streamer || 'AutoDJ'} · {log.playlist || 'N/A'} · {log.listeners} listeners</p></div>
                <span className="text-white/30 text-xs flex-shrink-0">{new Date(log.played_at * 1000).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAirplayTab;
